import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * GLP-1-quiz deriver. Wraps `deriveBoostProfile` (the cortisol pattern) with:
 *   1. Passthrough only: the GLP-1 quiz reuses quiz-v6 ids verbatim where
 *      genuinely equivalent (`age`, `body-composition`, `when-started`,
 *      `results-want`). There is no stress question, so unlike cortisol there is
 *      NO translation step. (The `energy-3pm` passthrough was dropped in the
 *      2026-06-12 copy pass when that question was removed from the quiz.)
 *   2. Sign severity: the GLP-1-only sign questions feed severity via the
 *      `extraSignals` seam (worst -> tier1, second-worst -> tier2). The
 *      passthrough ids (body-composition, age, when-started) are scored by the
 *      base deriver -- they are deliberately NOT in these maps to avoid
 *      double-counting.
 *   3. Forced primary: `catabolic-deficit` leads the verdict on every path via
 *      `forcePrimaryCause` -- the quiz's entry promise and both bridges name the
 *      rapid-weight-loss -> T-crash mechanism, so the verdict must too. The
 *      organic top scorer stays visible as Secondary.
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = [
  "age",
  "body-composition",
  "when-started",
  "results-want",
] as const;

/**
 * GLP-1 sign severity. Worst answer -> tier1, second-worst -> tier2. Tier
 * weights are the calibration knob (adjust here, never the base deriver); the
 * floor lock + persona-distribution snapshot gate the result.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  "glp1-face": ["yes-noticeably"],
  // `yes-muscle-loss` (explicit "lost muscle as well as fat") is the heaviest
  // arm-shrink answer, added in the 2026-06-12 copy pass -- tier1 alongside the
  // deflated-arms "yes-noticeably".
  "arm-shrink": ["yes-muscle-loss", "yes-noticeably"],
  "protein-intake": ["way-less"],
  libido: ["significantly"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "glp1-face": ["some"],
  "arm-shrink": ["somewhat"],
  "protein-intake": ["somewhat-less"],
  libido: ["somewhat"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
  };
}

/** Boost-namespace answer set the base deriver reads (passthrough only). */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  return out;
}

export function deriveQuizGlpProfile(answers: Answers): TSuppressionProfile {
  return deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "catabolic-deficit",
  });
}
